import React, { useState, useCallback, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { useSettingsSearchMatch } from '@/contexts/SettingsSearchContext';

/**
 * Collapsible Settings card.
 *
 * Wrap a settings section so users can collapse heavy content. State persists
 * per-section in localStorage under `settings_collapsed_<storageKey>`. Default
 * is expanded.
 *
 * Search-aware: when a SettingsSearchProvider is mounted higher in the tree
 * and a query is set, cards auto-expand if their title/subtitle/searchTerms
 * matches and hide if not.
 *
 * Usage:
 *   <CollapsibleSettingsCard
 *     storageKey="subscription"
 *     icon={<CreditCard ... />}
 *     title="Subscription"
 *     subtitle="Manage your plan & billing"
 *     glassCard={glassCard}
 *     testId="settings-subscription-section"
 *     tabId="account"            // for tab badge counts
 *     searchTerms="billing plan stripe paypal" // extra keywords beyond title/subtitle
 *   >
 *     ...content...
 *   </CollapsibleSettingsCard>
 */
const STORAGE_PREFIX = 'settings_collapsed_';

function readInitial(storageKey, defaultOpen) {
  try {
    const v = localStorage.getItem(STORAGE_PREFIX + storageKey);
    if (v === '1') return false; // explicitly collapsed
    if (v === '0') return true;  // explicitly expanded
  } catch {}
  return defaultOpen;
}

export default function CollapsibleSettingsCard({
  storageKey,
  icon,
  title,
  subtitle,
  badge,
  glassCard,
  testId,
  defaultOpen = true,
  className = '',
  rightAdornment,
  children,
  tabId = 'default',
  searchTerms = '',
}) {
  const [open, setOpen] = useState(() => readInitial(storageKey, defaultOpen));

  // Single haystack used both for search highlighting + tab match counts.
  const haystack = useMemo(
    () => `${title || ''} ${subtitle || ''} ${searchTerms || ''}`,
    [title, subtitle, searchTerms],
  );
  const { matches, isSearching } = useSettingsSearchMatch(tabId, storageKey, haystack);

  // When searching and not matching, hide the card entirely.
  const hidden = isSearching && !matches;
  // When searching and matching, force open regardless of persisted state.
  const effectiveOpen = isSearching ? matches : open;

  const toggle = useCallback(() => {
    if (isSearching) return; // disabled while searching
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_PREFIX + storageKey, next ? '0' : '1');
      } catch {}
      return next;
    });
  }, [storageKey, isSearching]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  if (hidden) return null;

  const matchHighlight = isSearching && matches
    ? { boxShadow: '0 0 0 1.5px var(--theme-accent)', borderColor: 'var(--theme-accent)' }
    : null;

  return (
    <div
      className={`rounded-3xl ${className}`}
      style={{ ...(glassCard || {}), ...(matchHighlight || {}) }}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={toggle}
        onKeyDown={onKeyDown}
        aria-expanded={effectiveOpen}
        className="w-full p-6 flex items-center gap-3 text-left bg-transparent"
        style={{
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        }}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        {icon && (
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'var(--glass-bg-active)' }}
          >
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-semibold truncate" style={{ color: 'var(--theme-text)' }}>
              {title}
            </h2>
            {badge}
          </div>
          {subtitle && (
            <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>
              {subtitle}
            </p>
          )}
        </div>
        {rightAdornment}
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform duration-300 ${effectiveOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--theme-textSecondary)' }}
        />
      </button>

      <div
        className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
        style={{
          maxHeight: effectiveOpen ? '4000px' : '0px',
          opacity: effectiveOpen ? 1 : 0,
        }}
        aria-hidden={!effectiveOpen}
        data-testid={testId ? `${testId}-content` : undefined}
      >
        <div className="px-6 pb-6 pt-0">
          {children}
        </div>
      </div>
    </div>
  );
}
